// ** Modules
import React from 'react';

// ** UI
import Box from '@mui/material/Box';
import MuiAppBar, { type AppBarProps } from '@mui/material/AppBar';
import { alpha, useTheme, styled } from '@mui/material/styles';

// ** Components
import ContentContainer from '@/components/common/ContentContainer';
import { HeaderLogo } from '@/components/common/HeaderMenuButton';

// ** Utils
import { logoSwitcher } from '@/utils/ui/styleUtils';

// ** Types
import type { User } from 'next-auth';
import type { AppContext } from '@/types/contextTypes';
import type { LayoutProps } from '@/types/layoutProps';
import type { SxProps, Theme } from '@mui/material/styles';

type HorizontalLayout = NonNullable<LayoutProps['horizontalLayoutProps']>;
type AppBarConfig = NonNullable<HorizontalLayout['appBar']>;

export type CustomAppBarProps = {
	settings: LayoutProps['settings'];
	saveSettings: LayoutProps['saveSettings'];
	appBarContent: AppBarConfig['content'];
	appBarProps: AppBarConfig['componentProps'];
	currentUser: User;
	host: AppContext['host'];
	path?: string;
	hideOnScroll?: boolean;
	scrollThresholdPx?: number;
	minDeltaPx?: number;
	hideOnMobile?: boolean;
	positionFixed?: boolean;
	containerMaxWidthPx?: number;
	containerDisableGutters?: boolean;
	containerSx?: SxProps<Theme>;
	sx?: SxProps<Theme>;
	logoAlt?: string;
	onMenuClick?: () => void;
};

type StyledAppBarProps = AppBarProps & {
	$isVisible: boolean;
	$isAtTop: boolean;
	$hideOnMobile: boolean;
};

const AppBar = styled(MuiAppBar, {
	shouldForwardProp: (prop) => !['$isVisible', '$isAtTop', '$hideOnMobile'].includes(prop as string)
})<StyledAppBarProps>(({ theme, $isVisible, $isAtTop, $hideOnMobile }) => ({
	display: $hideOnMobile ? 'none' : 'block',
	[theme.breakpoints.up('sm')]: {
		display: 'block'
	},
	position: 'fixed',
	top: 0,
	left: 0,
	right: 0,
	zIndex: 1400,
	transition: 'transform 220ms ease, background-color 220ms ease, backdrop-filter 220ms ease',
	willChange: 'transform, background-color',
	transform: $isVisible ? 'translateY(0)' : 'translateY(-110%)',
	backgroundColor: $isAtTop ? 'transparent' : alpha(theme.palette.common.black, 0.32),
	backdropFilter: $isAtTop ? 'none' : 'blur(12px)',
	WebkitBackdropFilter: $isAtTop ? 'none' : 'blur(12px)',
	borderBottom: '1px solid',
	borderBottomColor: $isAtTop ? 'transparent' : alpha(theme.palette.common.black, 0.12)
}));

const CustomAppBar = ({
	logoAlt = 'Logo',
	hideOnScroll = true,
	scrollThresholdPx = 80,
	minDeltaPx = 4,
	hideOnMobile = true,
	containerMaxWidthPx = 1440,
	containerDisableGutters = true,
	containerSx,
	positionFixed = true,
	sx,
	host,
	settings,
	appBarContent: userAppBarContent,
	onMenuClick,
	...props
}: CustomAppBarProps) => {
	const theme = useTheme();
	const [isVisible, setIsVisible] = React.useState(true);
	const [isAtTop, setIsAtTop] = React.useState(true);

	const lastScrollYRef = React.useRef(0);
	const latestScrollYRef = React.useRef(0);
	const tickingRef = React.useRef(false);
	const programmaticScrollRef = React.useRef(false);
	const navbarRef = React.useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		if (typeof window === 'undefined') return;

		const onProgrammaticScroll = (e: Event) => {
			programmaticScrollRef.current = (e as CustomEvent).detail;
		};
		window.addEventListener('programmatic-scroll', onProgrammaticScroll);
		return () => window.removeEventListener('programmatic-scroll', onProgrammaticScroll);
	}, []);

	React.useEffect(() => {
		if (typeof window === 'undefined') return;
		lastScrollYRef.current = window.scrollY;
		setIsAtTop(lastScrollYRef.current < 4);

		const update = () => {
			tickingRef.current = false;
			const y = latestScrollYRef.current;
			const lastY = lastScrollYRef.current;
			const delta = Math.abs(y - lastY);

			setIsAtTop(y < 4);

			if (delta >= minDeltaPx) {
				if (programmaticScrollRef.current) {
					lastScrollYRef.current = y;
					return;
				}

				if (!hideOnScroll || y < scrollThresholdPx) {
					setIsVisible(true);
				} else {
					setIsVisible(y <= lastY);
				}
				lastScrollYRef.current = y;
			}
		};

		const onScroll = () => {
			latestScrollYRef.current = window.scrollY;
			if (tickingRef.current) return;
			tickingRef.current = true;
			window.requestAnimationFrame(update);
		};

		window.addEventListener('scroll', onScroll, { passive: true });
		return () => window.removeEventListener('scroll', onScroll);
	}, [hideOnScroll, minDeltaPx, scrollThresholdPx]);

	React.useEffect(() => {
		if (typeof document === 'undefined') return;
		const el = navbarRef.current;
		const offset = isVisible && !isAtTop && el ? el.offsetHeight : 0;
		document.documentElement.style.setProperty('--scroll-navbar-offset', `${offset}px`);
	}, [isVisible, isAtTop]);

	if (settings.appBar === 'hidden') return null;

	const logo = host ? logoSwitcher(host) : '/images/drafts/logo.png';

	return (
		<React.Fragment>
			{hideOnMobile && (
				<Box
					sx={{
						display: { xs: 'block', sm: 'none' },
						position: 'absolute',
						top: 0,
						left: 0,
						right: 0,
						zIndex: 1400,
						pointerEvents: 'none'
					}}
				>
					<HeaderLogo
						logoSrc={logo}
						logoAlt={logoAlt}
						sx={{
							position: 'absolute',
							top: 24,
							left: theme.spacing(theme.layout.pagePx.xs),
							width: 56,
							height: 50,
							pointerEvents: 'auto'
						}}
					/>
				</Box>
			)}

			<AppBar
				ref={navbarRef}
				$isVisible={isVisible}
				$isAtTop={isAtTop}
				$hideOnMobile={hideOnMobile}
				sx={sx}
				elevation={0}
			>
				<ContentContainer
					maxWidthPx={containerMaxWidthPx}
					disableGutters={containerDisableGutters}
					sx={[
						...(containerSx ? [] : [(t: Theme) => ({ px: t.layout.pagePx })]),
						...(Array.isArray(containerSx) ? containerSx : containerSx ? [containerSx] : [])
					]}
				>
					{/* INJECTING isAtTop HERE */}
					{userAppBarContent?.({
						...props,
						onMenuClick,
						settings,
						isAtTop,
						host
					}) || null}
				</ContentContainer>
			</AppBar>
		</React.Fragment>
	);
};

export default CustomAppBar;
